import { useEffect, useState } from "react";
import { ExternalLink, Loader2, X } from "lucide-react";

export interface DocPreviewState {
  url: string;
  workerName: string;
}

function inferDocKind(url: string, mime: string | null): "pdf" | "image" | "other" {
  // MIME from the actual response is more trustworthy than the URL extension —
  // Azure Blob URLs sometimes don't carry an extension at all.
  if (mime) {
    if (mime === "application/pdf") return "pdf";
    if (mime.startsWith("image/")) return "image";
  }
  const path = url.split("?")[0].toLowerCase();
  if (path.endsWith(".pdf")) return "pdf";
  if (/\.(jpe?g|png|webp|gif)$/.test(path)) return "image";
  return "other";
}

export function DocumentPreviewModal({
  state,
  onClose,
}: {
  state: DocPreviewState;
  onClose: () => void;
}) {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [blobMime, setBlobMime] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const kind = inferDocKind(state.url, blobMime);

  // Fetch the doc and convert to a blob URL. Browsers don't apply
  // X-Frame-Options or X-Content-Type-Options to blob: URLs, so the
  // PDF/image renders even though our backend ships every response with
  // X-Frame-Options: DENY + nosniff.
  useEffect(() => {
    let cancelled = false;
    let createdUrl: string | null = null;
    setBlobUrl(null);
    setBlobMime(null);
    setError(null);
    // SAS URLs auth via the signature query param — sending cookies would just complicate CORS.
    fetch(state.url)
      .then(async (res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status} ${res.statusText}`);
        const declaredType = res.headers.get("content-type");
        const blob = await res.blob();
        if (cancelled) return;
        // Some servers respond with `application/octet-stream` when the
        // file extension is unknown — re-tag the blob as PDF if the URL
        // says .pdf so the browser's PDF viewer activates.
        const url = state.url.toLowerCase();
        let effectiveBlob = blob;
        let effectiveMime = declaredType ?? blob.type;
        if (url.endsWith(".pdf") && !effectiveMime.includes("pdf")) {
          effectiveBlob = new Blob([await blob.arrayBuffer()], {
            type: "application/pdf",
          });
          effectiveMime = "application/pdf";
        }
        createdUrl = URL.createObjectURL(effectiveBlob);
        setBlobUrl(createdUrl);
        setBlobMime(effectiveMime);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const msg = e instanceof Error ? e.message : "Failed to load document";
        // Cross-origin Azure Blob without CORS shows up as a TypeError
        // here. Tell the user to use the "Open in new tab" link instead
        // of staring at a generic error.
        setError(msg);
      });
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [state.url]);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex animate-fade-in items-center justify-center bg-overlay/60 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="flex h-[90vh] w-full max-w-4xl animate-scale-in flex-col overflow-hidden rounded-xl border border-border bg-surface-raised shadow-elevated"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-copy">
              ID document — {state.workerName}
            </p>
            <p className="text-xs text-copy-subtle">
              Verify it matches the legal status and SIN on file.
            </p>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <a
              href={state.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-2 py-1 text-xs hover:bg-surface-sunken"
            >
              <ExternalLink className="h-3 w-3" />
              Open in new tab
            </a>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-surface hover:bg-surface-sunken"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        <div className="flex flex-1 items-center justify-center overflow-auto bg-surface-sunken">
          {error ? (
            <div className="p-6 text-center text-sm text-destructive-foreground">
              <p>Could not load document: {error}</p>
              <a
                href={state.url}
                target="_blank"
                rel="noreferrer"
                className="mt-2 inline-block text-primary hover:underline"
              >
                Try opening it directly
              </a>
            </div>
          ) : !blobUrl ? (
            <div className="flex items-center gap-2 p-6 text-sm text-copy-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading document…
            </div>
          ) : kind === "image" ? (
            <img
              src={blobUrl}
              alt="ID document"
              className="max-h-full max-w-full object-contain"
            />
          ) : kind === "pdf" ? (
            // <embed> handles PDFs more reliably than <iframe> across
            // Chrome/Firefox/Safari — iframe sometimes shows a download
            // prompt instead of activating the built-in PDF viewer.
            <embed
              src={blobUrl}
              type="application/pdf"
              title="ID document"
              className="h-full w-full"
            />
          ) : (
            <div className="space-y-3 p-6 text-center text-sm text-copy-muted">
              <p>
                Preview not available for this file type
                {blobMime ? ` (${blobMime})` : ""}.
              </p>
              <div className="flex justify-center gap-2">
                <a
                  href={blobUrl}
                  download
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-primary hover:bg-surface-sunken"
                >
                  Download
                </a>
                <a
                  href={state.url}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-md border border-border bg-surface px-3 py-1.5 text-xs text-primary hover:bg-surface-sunken"
                >
                  <ExternalLink className="h-3 w-3" /> Open in new tab
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
